'use client';

import { createClient } from '@/lib/supabase/client';

export interface Part {
  id: string;
  partNumber: string;
  name: string;
  category: string;
  unitPrice: number;
  stock: number;
  minStock: number;
  lastUpdated: string;
}

// ── Supabase helpers ──────────────────────────────────────────────────────────

function rowToPart(row: Record<string, unknown>): Part {
  return {
    id: row.id as string,
    partNumber: row.part_number as string,
    name: row.name as string,
    category: row.category as string,
    unitPrice: (row.unit_price as number) || 0,
    stock: (row.stock as number) || 0,
    minStock: (row.min_stock as number) || 5,
    lastUpdated: (row.last_updated as string) || '',
  };
}

export async function getParts(): Promise<Part[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('parts')
    .select('*')
    .order('part_number', { ascending: true });
  if (error) {
    console.error('getParts error:', error.message);
    return [];
  }
  return (data || []).map((r) => rowToPart(r as Record<string, unknown>));
}

export async function addPart(part: Omit<Part, 'id' | 'lastUpdated'>): Promise<Part> {
  const supabase = createClient();
  const id = `part-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const lastUpdated = new Date().toLocaleDateString('en-ZA');
  const { data, error } = await supabase
    .from('parts')
    .insert({
      id,
      part_number: part.partNumber,
      name: part.name,
      category: part.category,
      unit_price: part.unitPrice,
      stock: part.stock,
      min_stock: part.minStock,
      last_updated: lastUpdated,
    })
    .select()
    .single();
  if (error) {
    console.error('addPart error:', error.message);
    throw error;
  }
  return rowToPart(data as Record<string, unknown>);
}

export async function updatePart(id: string, updates: Partial<Part>): Promise<void> {
  const supabase = createClient();
  const dbUpdates: Record<string, unknown> = {};
  if (updates.partNumber !== undefined) dbUpdates.part_number = updates.partNumber;
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.category !== undefined) dbUpdates.category = updates.category;
  if (updates.unitPrice !== undefined) dbUpdates.unit_price = updates.unitPrice;
  if (updates.stock !== undefined) dbUpdates.stock = updates.stock;
  if (updates.minStock !== undefined) dbUpdates.min_stock = updates.minStock;
  dbUpdates.last_updated = new Date().toLocaleDateString('en-ZA');
  const { error } = await supabase.from('parts').update(dbUpdates).eq('id', id);
  if (error) console.error('updatePart error:', error.message);
}

export async function deletePart(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('parts').delete().eq('id', id);
  if (error) console.error('deletePart error:', error.message);
}

export async function bulkImportParts(
  incoming: Omit<Part, 'id' | 'lastUpdated'>[]
): Promise<Part[]> {
  const supabase = createClient();
  const today = new Date().toLocaleDateString('en-ZA');
  const rows = incoming.map((p) => ({
    id: `part-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    part_number: p.partNumber,
    name: p.name,
    category: p.category,
    unit_price: p.unitPrice,
    stock: p.stock,
    min_stock: p.minStock,
    last_updated: today,
  }));
  const { data, error } = await supabase
    .from('parts')
    .upsert(rows, { onConflict: 'part_number', ignoreDuplicates: true })
    .select();
  if (error) {
    console.error('bulkImportParts error:', error.message);
    return [];
  }
  return (data || []).map((r) => rowToPart(r as Record<string, unknown>));
}

const SEED_PARTS: Omit<Part, 'id' | 'lastUpdated'>[] = [
  { partNumber: 'TB009',      name: 'CIT BAT NODE BOARD V1.2',                                          category: 'Boards',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TB003',      name: 'CIT NODE BOARD V2.0',                                              category: 'Boards',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TB005',      name: 'CIT RETRO DOOR BOARD V1.0',                                        category: 'Boards',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TB006',      name: 'RETRO LOCK BOARD V1.0',                                            category: 'Boards',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TA011-400',  name: 'SLOTTED OPTO SENSOR BOARD ASSEMBLY STANDARD 0.4M',                 category: 'Sensors',  unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC003',      name: 'NORDSON DEADBOLT LOCK BODY',                                       category: 'Locks',    unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC053',      name: 'SLAMLOCK 5 STANDARD',                                              category: 'Locks',    unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TA002',      name: 'DOOR LOOP ASSEMBLY WHITE MINIFIT',                                  category: 'Harnesses', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC004',      name: 'NORDSON SWINGBOLT LOCK BODY',                                      category: 'Locks',    unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TA010-1800', name: 'DOOR SENSOR TX NO HOUSING BLACK MICROFIT 1.8M',                    category: 'Sensors',  unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TH018',      name: 'BUS SPLITTER MINIFIT WHITE',                                       category: 'Harnesses', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TK021',      name: 'CREW DOOR REPLACEMENT KIT MK4 COMPATIBLE HARNESS',                 category: 'Kits',     unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TK022',      name: 'VAULT DOOR REPLACEMENT KIT MK4 COMPATIBLE HARNESS',                category: 'Kits',     unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TK023',      name: 'SAFE OR DROP BIN REPLACEMENT KIT MK4 COMPATIBLE HARNESS',          category: 'Kits',     unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC059',      name: 'POWER CONDITIONING UNIT',                                          category: 'Boards',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TH001-5000', name: 'BUS CABLE 5M',                                                     category: 'Cables',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TH038',      name: 'LOCK HARNESS RETRO ADAPTOR',                                       category: 'Harnesses', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TH001-1000', name: 'BUS CABLE 1M',                                                     category: 'Cables',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC066',      name: 'SERVO MOTOR TOWER PRO MG996-9KG',                                  category: 'Electronics', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TH026-300',  name: 'HARNESS BAT LIMIT SWITCH 300MM',                                   category: 'Harnesses', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TB004',      name: 'CIT OPTO INTERFACE BOARD V1.1',                                    category: 'Boards',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC045',      name: 'IR RX SENSOR TSOP34838',                                           category: 'Sensors',  unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC008',      name: 'INFRA RED LED TX 5MM SFH4556',                                     category: 'Electronics', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TH001-7000', name: 'BUS CABLE 7M',                                                     category: 'Cables',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TH001-100',  name: 'BUS CABLE 0.1M',                                                   category: 'Cables',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TH001-2500', name: 'BUS CABLE 2.5M',                                                   category: 'Cables',   unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TK017',      name: 'RETRO 12 DOOR KIT',                                                category: 'Kits',     unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TK018',      name: 'RETRO 4 DOOR KIT',                                                 category: 'Kits',     unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC075',      name: 'CRIMP TERMINAL FEMALE MICROFIT',                                    category: 'Electronics', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TC076',      name: 'CRIMP TERMINAL MALE MICROFIT',                                      category: 'Electronics', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TM010',      name: 'LOCK SOLENOID NI-500A',                                             category: 'Locks',    unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TT004',      name: 'NODE PROGRAMMER',                                                   category: 'Electronics', unitPrice: 0, stock: 0, minStock: 5 },
  { partNumber: 'TP002',      name: 'HMI KIT 10M BUS',                                                  category: 'Kits',     unitPrice: 0, stock: 0, minStock: 5 },
];

export async function seedDefaultParts(): Promise<{ added: number; skipped: number }> {
  const existing = await getParts();
  const existingNums = new Set(existing.map((e) => e.partNumber.toUpperCase()));
  const toAdd = SEED_PARTS.filter((p) => !existingNums.has(p.partNumber.toUpperCase()));
  if (toAdd.length === 0) return { added: 0, skipped: SEED_PARTS.length };
  const added = await bulkImportParts(toAdd);
  return { added: added.length, skipped: SEED_PARTS.length - added.length };
}

// ── localStorage fallback ─────────────────────────────────────────────────────
const STORAGE_KEY = 'fss_parts_v2';

export function getPartsLocal(): Part[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function clearAllParts(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: clearAllParts is not implemented yet.', args);
  return null;
}

export { clearAllParts };